import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Arrow, Bar, Dot, mix, Pill, Text, Tile } from './illustration_parts';

export const ON_ITS_WAY_ILLUSTRATION_WIDTH = 746;

export default function AgenticCommerceOnItsWayIllustration({ scale }) {
  return (
    <Canvas $scale={scale} aria-hidden="true">
      <EngagementTile />
      <OrdersTile />
      <RevenueTile />
    </Canvas>
  );
}

AgenticCommerceOnItsWayIllustration.propTypes = {
  scale: PropTypes.number.isRequired,
};

function EngagementTile() {
  const { t } = useTranslation();

  return (
    <Tile $height={314} $left={0} $top={110} $width={400}>
      <Text $left={146} $size={12} $top={13} $weight="mediumHeavy">
        {t('Total engagement')}
      </Text>
      {[79, 139, 199, 259, 319].map(left => (
        <Bar $color="gray200" $height={260} $left={left} $top={39} $width={1} key={left} />
      ))}
      <FunnelRow $color="green100" $darkColor="green200" label={t('Offered Chat')} top={41} width={331} />
      <FunnelRow $color="green300" label={t('Engaged with Chat')} top={77} width={216} />
      <FunnelRow isRecommendation label={t('Got a recommendation')} top={113} width={173} />
      <FunnelRow $color="green600" label={t('Clicked a product')} top={149} width={144} />
      <Text $color="gray700" $left={19} $size={8} $top={182}>
        {t('Share of engaged with Chat')}
      </Text>
      <Bar $color="gray300" $height={1} $left={139} $top={187} $width={240} />
      <FunnelRow $color="green200" label={t('Added to cart in Chat')} top={203} width={58} />
      <FunnelRow $color="green300" label={t('Reached checkout from Chat')} top={239} width={50} />
    </Tile>
  );
}

function FunnelRow({ $color, $darkColor, isRecommendation, label, top, width }) {
  const FunnelBar = isRecommendation ? RecommendationBar : Pill;

  return (
    <React.Fragment>
      <Text $color="gray800" $left={19} $size={9} $top={top}>
        {label}
      </Text>
      <FunnelBar
        $color={$color}
        $darkColor={$darkColor}
        $height={9}
        $left={19}
        $radius={100}
        $top={top + 13}
        $width={width}
      />
    </React.Fragment>
  );
}

FunnelRow.propTypes = {
  $color: PropTypes.string,
  $darkColor: PropTypes.string,
  isRecommendation: PropTypes.bool,
  label: PropTypes.string.isRequired,
  top: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
};

function OrdersTile() {
  const { t } = useTranslation();
  const legend = [
    { color: 'green400', label: t('Shopping conversations'), left: 19 },
    { color: 'gray700', label: t('Service only'), left: 139 },
    { color: 'gray500', label: t('Small talk only'), left: 259 },
    { color: 'gray400', label: t('Handed off'), left: 379 },
  ];

  return (
    <Tile $height={140} $left={190} $top={380} $width={520}>
      <Text $left={199} $size={12} $top={13} $weight="mediumHeavy">
        {t('Orders within 7 days')}
      </Text>
      <Pill $color="gray300" $left={149} $top={33} $width={220} />
      <Bar $color="green400" $height={22} $left={19} $top={51} $width={432} />
      <Bar $color="gray700" $height={22} $left={451} $top={51} $width={24} />
      <Bar $color="gray500" $height={22} $left={475} $top={51} $width={14} />
      <Bar $color="gray400" $height={22} $left={489} $top={51} $width={10} />
      {legend.map(({ color, label, left }) => (
        <React.Fragment key={left}>
          <Dot $color={color} $left={left} $top={87} />
          <Text $color="gray800" $left={left + 11} $size={8} $top={84}>
            {label}
          </Text>
          <Pill $color="gray400" $left={left + 11} $top={101} $width={34} />
        </React.Fragment>
      ))}
    </Tile>
  );
}

function RevenueTile() {
  const { t } = useTranslation();

  return (
    <Tile $height={260} $left={360} $top={0} $width={300}>
      <Text $left={95} $size={12} $top={15} $weight="medium">
        {t('Attributed revenue')}
      </Text>
      <Pill $color="green400" $height={26} $left={74} $radius={100} $top={41} $width={150} />
      <Pill $color="gray300" $height={7} $left={94} $radius={4} $top={77} $width={110} />
      <Arrow $left={114} $size={10} $top={89}>
        ↑
      </Arrow>
      <Pill $color="green200" $left={124} $top={92} $width={60} />
      <ChartArea />
      <ChartLine />
    </Tile>
  );
}

// The revenue trend from the design, as [x, y] in a 300×152 box.
const TREND = [
  [0, 96],
  [50, 109],
  [100, 77],
  [150, 64],
  [200, 38],
  [250, 19],
  [300, 0],
];
const LINE_HALF_WIDTH = 1.25;

function polygon(points) {
  return `polygon(${points.map(([x, y]) => `${x}px ${y}px`).join(', ')})`;
}

const Canvas = styled.div`
  height: 520px;
  margin: 18px;
  position: relative;
  width: 710px;
  zoom: ${p => p.$scale};
`;

// The design's light-theme color here (#3E9B3E) sits between two tokens; the dark theme uses green400.
const RecommendationBar = styled(Pill)`
  background-color: color-mix(in srgb, ${p => p.theme.colors.green300}, ${p => p.theme.colors.green500});

  :root[data-theme='dark'] & {
    background-color: ${p => p.theme.colors.green400};
  }
`;

const chartBox = `
  height: 152px;
  left: -1px;
  position: absolute;
  top: 117px;
  width: 300px;
`;

const ChartArea = styled.div`
  ${chartBox};
  background: linear-gradient(${p => mix(p, 'green400', 22)}, ${p => mix(p, 'green400', 2)});
  clip-path: ${polygon([...TREND, [300, 152], [0, 152]])};
`;

const ChartLine = styled.div`
  ${chartBox};
  background-color: ${p => p.theme.colors.green400};
  clip-path: ${polygon([
    ...TREND.map(([x, y]) => [x, y - LINE_HALF_WIDTH]),
    ...[...TREND].reverse().map(([x, y]) => [x, y + LINE_HALF_WIDTH]),
  ])};
`;
